import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';

import { useI18n } from '@/i18n/LanguageContext';
import { SMS_CONSENT_TEXT } from '@/data/smsConsent';

/**
 * A2P 10DLC SMS CONSENT CHECKBOX.
 *
 * Rules enforced by this component (do not weaken):
 *   - The parent owns the state and MUST initialise it to `false`.
 *   - It is never `required` and never blocks a submit — consent is not a
 *     condition of registration, contact or purchase.
 *   - It is a SEPARATE control from the Terms acceptance checkbox.
 *   - The visible copy is the exact versioned disclosure from
 *     src/data/smsConsent.ts, with working links to Privacy and Terms.
 */
interface Props {
  checked: boolean;
  onChange: (v: boolean) => void;
  variant?: 'light' | 'dark' | 'boxed';
  className?: string;
  id?: string;
}

const SmsConsentCheckbox: React.FC<Props> = ({ checked, onChange, variant = 'light', className = '', id }) => {
  const { lang, path } = useI18n();
  const l = lang === 'es' ? 'es' : 'en';
  const full = SMS_CONSENT_TEXT[l];
  const cut = full.lastIndexOf(l === 'es' ? 'Consulte nuestra' : 'View our');
  const body = cut > 0 ? full.slice(0, cut).trim() : full;
  const dark = variant === 'dark';
  const autoId = React.useId();
  const inputId = id || `sms-consent-${autoId.replace(/[^a-zA-Z0-9]/g, '')}`;

  const linkCls = dark ? 'font-semibold text-orange-300 underline' : 'font-semibold text-orange-600 underline';

  return (
    <div
      className={`${
        variant === 'boxed' ? 'rounded-xl border border-slate-200 bg-slate-50 p-3' : ''
      } ${className}`}
    >
      <label
        htmlFor={inputId}
        className={`flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed ${
          dark ? 'text-slate-400' : 'text-slate-600'
        }`}
      >
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-orange-500"
          aria-describedby={`${inputId}-hint`}
        />
        <span>
          <span className={`mb-0.5 flex items-center gap-1 font-bold ${dark ? 'text-slate-200' : 'text-slate-800'}`}>
            <MessageSquare className="h-3.5 w-3.5" />
            {l === 'es' ? 'Mensajes de texto (opcional)' : 'Text messages (optional)'}
          </span>
          {body}{' '}
          {l === 'es' ? 'Consulte nuestra ' : 'View our '}
          <Link to={path('/privacy')} target="_blank" rel="noopener" className={linkCls} onClick={(e) => e.stopPropagation()}>
            {l === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
          </Link>
          {l === 'es' ? ' y ' : ' and '}
          <Link to={path('/terms')} target="_blank" rel="noopener" className={linkCls} onClick={(e) => e.stopPropagation()}>
            {l === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
          </Link>
          .
        </span>
      </label>
      <p id={`${inputId}-hint`} className="sr-only">
        {l === 'es' ? 'Opcional. No es necesario para continuar.' : 'Optional. Not required to continue.'}
      </p>
    </div>
  );
};

export default SmsConsentCheckbox;
